import { useCallback } from 'react'
import { useSetSpeakerMutation, BroadcastVideoLayout } from 'graphql/generated'
import { useEventVideoContext } from 'context/EventVideoContext'
import { useQueryErrorHandler } from 'hooks'
import { useOptionsModal } from 'components/modals/OptionsModal'
import {
  faExpand,
  faGripLines,
  faGripLinesVertical,
  faImages,
  faStopCircle
} from '@fortawesome/pro-regular-svg-icons'

function useSetSpeaker(
  userId: string,
  onCompleted?: Callback
): [Callback, boolean, Callback] {
  const { event, currentSpeakers, currentLayout } = useEventVideoContext()
  const eventId = event.id

  const numberOfSpeakers = currentSpeakers?.length || 0
  const firstSpeaker = currentSpeakers?.[0]

  const onError = useQueryErrorHandler(
    'Failed to set speaker. Please try again.'
  )
  const [setSpeaker, { loading }] = useSetSpeakerMutation({
    onError,
    onCompleted
  })

  const showOptions = useOptionsModal()
  const showLayoutMenu = useCallback(
    () =>
      showOptions({
        title: 'Presenter Controls',
        body: 'Select the window layout for this presenter',
        options: [
          numberOfSpeakers > 0 && {
            icon: faStopCircle,
            label:
              numberOfSpeakers === 1
                ? `End ${firstSpeaker?.shortDisplayName}`
                : `End All Presenters`,
            onClick: () =>
              setSpeaker({
                variables: {
                  eventId,
                  layout: BroadcastVideoLayout.HOST
                }
              })
          },
          currentLayout !== BroadcastVideoLayout.PRESENTER && {
            icon: faExpand,
            label: `Full Screen Presenter`,
            onClick: () =>
              setSpeaker({
                variables: {
                  eventId,
                  userId,
                  layout: BroadcastVideoLayout.PRESENTER
                }
              })
          },
          currentLayout !== BroadcastVideoLayout.PIP && {
            icon: faImages,
            label: `Picture-in-picture`,
            onClick: () =>
              setSpeaker({
                variables: {
                  eventId,
                  userId,
                  layout: BroadcastVideoLayout.PIP
                }
              })
          },
          currentLayout !== BroadcastVideoLayout.SPLIT_HORIZONTAL && {
            icon: faGripLines,
            label: `Split Screen (Horizontally)`,
            onClick: () =>
              setSpeaker({
                variables: {
                  eventId,
                  userId,
                  layout: BroadcastVideoLayout.SPLIT_HORIZONTAL
                }
              })
          },
          currentLayout !== BroadcastVideoLayout.SPLIT_VERTICAL && {
            icon: faGripLinesVertical,
            label: `Split Screen (Vertically)`,
            onClick: () =>
              setSpeaker({
                variables: {
                  eventId,
                  userId,
                  layout: BroadcastVideoLayout.SPLIT_VERTICAL
                }
              })
          }
        ]
      }),
    [
      currentLayout,
      eventId,
      setSpeaker,
      showOptions,
      numberOfSpeakers,
      firstSpeaker,
      userId
    ]
  )

  const showMenu = useCallback(() => {
    // failsafe; only hosts can invite new speakers
    if (!event.isPrivileged) {
      return
    }
    // host is adding another (n>1) presenter, skip the
    // layout menu, and just add the new presenter in grid layout
    else if (numberOfSpeakers > 0) {
      setSpeaker({
        variables: {
          eventId,
          userId,
          layout: BroadcastVideoLayout.GRID
        }
      })
      return
    }

    // show full menu when host is adding the first speaker
    showLayoutMenu()
  }, [
    eventId,
    userId,
    event.isPrivileged,
    showLayoutMenu,
    setSpeaker,
    numberOfSpeakers
  ])

  return [showMenu, loading, showLayoutMenu]
}

export default useSetSpeaker
